import { Scene } from "three";
import type { GLTF } from "three/examples/jsm/Addons.js";
import DiagnosticsStore from "@/features/diagnostics/store/diagnostics-store.ts";
import ModelNotReadyException from "@/features/model-renderer/exceptions/ModelNotReadyException.ts";
import disposeChild from "@/features/model-renderer/experience/utils/dispose-scene.ts";
import debugModel from "@/features/model-renderer/utils/debug-model.ts";
import type { Part, SmartNumberMapping } from "@/features/sub-families/types";
import Logger from "@/lib/logger.ts";
import type { Nullable, Nullish } from "@/types";
import ModelMap from "../lib/ModelMap.ts";
import { renderModelByConfiguration } from "../utils/render-model-by-configuration.ts";
import Camera from "./Camera";
import Renderer from "./Renderer";
import { getSources } from "./sources";
import LoadingBar from "./utils/LoadingBar";
import Resources from "./utils/Resources";
import Sizes from "./utils/Sizes";
import Time from "./utils/Time";
import World from "./world/World";

export default class Experience {
    readonly #canvas: HTMLCanvasElement;

    // Scene
    readonly #scene: Scene;
    #loadingBar: Nullish<LoadingBar>;

    // Utils
    readonly #sizes: Sizes;
    readonly #time: Time;

    readonly #renderer: Renderer;
    #camera: Nullable<Camera> = null;

    #destroyed: boolean = false;
    #needsRender: boolean = false;
    readonly #loadStart: number;

    readonly #triggerOnLoadComplete: (model: GLTF) => void;

    constructor(canvas: HTMLCanvasElement, path: string, onLoadComplete?: (model: GLTF) => void) {
        this.#canvas = canvas;

        this.#triggerOnLoadComplete = (model: GLTF) => {
            onLoadComplete?.(model);
        };

        this.#scene = new Scene();

        this.#loadingBar = new LoadingBar(this.#scene);
        this.#loadingBar.on("dispose", this.#destroyLoadingBar);

        this.#sizes = new Sizes(this.#canvas);
        this.#time = new Time();

        this.#renderer = new Renderer(this.#canvas, this.#sizes);

        this.#loadStart = performance.now();

        Logger.info("Loading resources");
        DiagnosticsStore.collectEventLog("info", "[Experience] Loading resources");

        const sources = getSources(path);

        const gltfUrl = new URL(path, window.location.origin);

        Resources.getInstance().createLoadersFromOrigin(gltfUrl.origin);
        Resources.getInstance().init(sources);
        Resources.getInstance().on("progress", this.#loadingProgress);
        Resources.getInstance().on("ready", this.#resourcesReady);
    }

    async updateModel(smartNumberMapping: SmartNumberMapping) {
        const model = Resources.getInstance().model;
        if (!model) throw new ModelNotReadyException();

        renderModelByConfiguration(model, smartNumberMapping);
        this.requestRender();
    }

    debugModel(expectedParts: Part[]) {
        debugModel(Resources.getInstance().model, expectedParts);
    }

    requestRender = (): void => {
        this.#needsRender = true;
    };

    zoomIn() {
        this.#camera?.zoomIn();
        this.requestRender();
    }

    zoomOut() {
        this.#camera?.zoomOut();
        this.requestRender();
    }

    destroy = () => {
        this.#destroyed = true;

        this.#scene.traverse(disposeChild);
        this.#scene.clear();

        Resources.getInstance().destroy();
        ModelMap.getInstance().destroy();

        this.#time.off("tick", this.#tick);
        this.#time.destroy();

        this.#sizes.off("resize", this.#resize);
        this.#sizes.destroy();

        this.#camera?.destroy();
        this.#renderer.dispose();

        this.#destroyLoadingBar();
    };

    getSnapShot() {
        if (this.#camera?.instance) {
            this.#renderer.renderFrame(this.#scene, this.#camera.instance);
        }

        return this.#renderer.getSnapShot();
    }

    #resize = () => {
        if (!this.#camera) return;

        this.#camera.resize(this.#sizes);
        this.#renderer.resize(this.#sizes);
        this.requestRender();
    };

    #tick = () => {
        if (!this.#camera?.instance) return;

        this.#camera.update();

        if (this.#needsRender || this.#loadingBar) {
            this.#renderer.render(this.#scene, this.#camera.instance, this.#time.deltaTime);
            this.#needsRender = false;
        }
    };

    #destroyLoadingBar = () => {
        Resources.getInstance().off("progress", this.#loadingProgress);
        Resources.getInstance().off("ready", this.#resourcesReady);

        if (this.#loadingBar) {
            this.#loadingBar.off("dispose", this.#destroyLoadingBar);
            this.#loadingBar = undefined;
        }

        this.requestRender();
    };

    #loadingProgress = () => {
        if (this.#destroyed) return;

        this.#loadingBar!.onProgress(Resources.getInstance().progress);
    };

    #resourcesReady = () => {
        if (this.#destroyed) return;

        DiagnosticsStore.collectModelLoadTime(performance.now() - this.#loadStart);

        Logger.success("Loaded resources");
        DiagnosticsStore.collectEventLog("success", "[Experience] Loaded resources");

        this.#loadingBar!.onLoadComplete();

        const model = Resources.getInstance().model;
        if (!model) throw new ModelNotReadyException();

        ModelMap.getInstance()
            .createFromModel(model)
            .then(() => {
                Logger.success("Successfully created model map!");
                DiagnosticsStore.collectEventLog("success", "[Experience] Created model map");
            })
            .catch((err) => {
                Logger.error("Failed creating model map", err);
                DiagnosticsStore.collectEventLog("error", "Failed creating model map");
            })
            .finally(() => {
                if (this.#destroyed) return;

                this.#createWorld(model, () => {
                    if (this.#destroyed) return;

                    this.#sizes.on("resize", this.#resize);
                    this.#time.on("tick", this.#tick);

                    this.#resize();
                    this.requestRender();

                    Logger.success("World created!");
                    DiagnosticsStore.collectEventLog("success", "[Renderer] Created 3D world");

                    this.#triggerOnLoadComplete(model);
                });
            });
    };

    #createWorld(model: GLTF, onReady: () => void) {
        const world = new World(this.#scene);
        world.init();

        const camera = new Camera(this.#canvas, this.#scene, this.requestRender);
        camera.createCamera(model, onReady);

        this.#camera = camera;
    }
}
